import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { OutlinedTextFieldProps, TextField, Typography } from "@mui/material";

import { useDebounce } from "../../hooks/useDebounce";
import { applyCurrencyMask, applyVolumeMask } from "../../utils";

interface TextFieldCustomProps extends Omit<OutlinedTextFieldProps, "variant"> {
  label?: string;
  hasError?: boolean;
  messageError?: string;
  endComponent?: React.ReactNode;
  isDisabled?: boolean;
  numericOnly?: boolean;
  maxLength?: number;
  mask?: string;
  minRows?: number;
  maxRows?: number;
}

const TextFieldCustom: React.FC<TextFieldCustomProps> = ({
  label,
  hasError,
  messageError,
  endComponent,
  value = "",
  isDisabled = false,
  rows = 0,
  onChange,
  numericOnly = false,
  maxLength,
  mask,
  minRows,
  maxRows,
  ...props
}) => {
  const { sx: sxProps } = props;

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [text, setText] = useState(value || "");
  const [inputError, setInputError] = useState("");

  const debouncedText = useDebounce(text, 500);
  const debouncedValue = useDebounce(value, 500);

  // NOTE: Aplica debounce atualizando o estado do componente pai apos delay
  useEffect(() => {
    if (debouncedText !== value) {
      const valueNew =
        value === "" && debouncedValue !== "" ? "" : debouncedText;

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      onChange?.({
        target: {
          value: valueNew,
        },
      } as ChangeEvent<HTMLInputElement>);

      handleOnChange(valueNew as string);
    }
  }, [debouncedText, value]);

  // Note: Sempre que a URL mudar, limpa o input (apenas para campos editaveis)
  useEffect(() => {
    if (isDisabled) return;
    setText("");
  }, [location.pathname]);

  const addMask = (value: string) => {
    switch (mask) {
      case "currency":
        return applyCurrencyMask(value);

      case "volume":
        return applyVolumeMask(value);

      default:
        return value;
    }
  };

  const handleOnChange = (value: string) => {
    const formatedValue = addMask(value);
    const hasOnlyNumbers = /^\d*$/.test(formatedValue);

    if (numericOnly) {
      setInputError(hasOnlyNumbers ? "" : "Somente números são permitidos");
      setText(hasOnlyNumbers ? formatedValue : "");
    } else {
      setText(formatedValue);
    }
  };

  return (
    <>
      {label && (
        <Typography
          fontSize={14}
          fontWeight={700}
          color={"shadesOfDark.dark"}
          mb={0.5}
          fontFamily={"Open Sans"}
          noWrap
        >
          {label}
        </Typography>
      )}
      <TextField
        disabled={isDisabled}
        fullWidth
        variant="outlined"
        size="small"
        autoComplete="off"
        value={text}
        rows={isDisabled ? undefined : rows}
        minRows={minRows}
        maxRows={maxRows}
        onChange={(e) => handleOnChange(e.target.value)}
        sx={{
          ...sxProps,
          width: "100%",
          boxShadow: "none",
          bgcolor: isDisabled ? "shadesOfDark.ultraLight" : "background.paper",
          color: "shadesOfDark.black",
          p: 0,
          fontSize: 14,
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          "& .MuiOutlinedInput-root.Mui-disabled": {
            backgroundColor: "shadesOfDark.ultraLight",
            "& > input": {
              WebkitTextFillColor: "shadesOfDark.steel",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "shadesOfDark.light",
            },
          },
          "& .MuiOutlinedInput-root": {
            background: isDisabled
              ? "shadesOfDark.ultraLight"
              : "background.paper",
            borderRadius: 1,
            color: "shadesOfDark.dark",
            "& fieldset": {
              borderColor: hasError
                ? "systemColors.error.main"
                : "shadesOfDark.light",
            },
            "& > input": {
              padding: 1.09,
              cursor: isDisabled ? "not-allowed" : "pointer",
            },
            "&.Mui-focused fieldset": {
              borderColor: hasError
                ? "systemColors.error.main"
                : "shadesOfDark.light",
            },
            "& textarea:disabled": {
              cursor: isDisabled ? "not-allowed" : "pointer",
            },
            "& .MuiOutlinedInput-input.Mui-disabled": {
              color: "shadesOfDark.black",
              opacity: 0.7,
              WebkitTextFillColor: "inherit",
            },
          },
        }}
        InputProps={{
          inputRef: textAreaRef,
          endAdornment: <>{endComponent}</>,
        }}
        inputProps={{
          inputMode: numericOnly ? "numeric" : undefined,
          pattern: numericOnly ? "[0-9]*" : undefined,
          ...(maxLength ? { maxLength } : {}),
        }}
        {...props}
      />
      {(hasError || inputError) && (
        <Typography
          mt={0.5}
          fontFamily={"Open Sans"}
          fontSize={10}
          color={"systemColors.error.main"}
        >
          {messageError || inputError}
        </Typography>
      )}
    </>
  );
};

export default TextFieldCustom;
